import React, { useState } from "react";
import { X, Leaf, Mail, Smartphone, Monitor, Moon, ChevronDown } from "lucide-react";

const EcologiaDigitaleArticle = ({ isOpen, onClose }) => {
  const [expanded, setExpanded] = useState(null);

  if (!isOpen) return null;

  const toggle = (i) => setExpanded(expanded === i ? null : i);

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#0c1220] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="ecologia-article"
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#0c1220] border-b border-white/10 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#34A853] rounded-xl flex items-center justify-center">
              <Leaf className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-black text-lg">Ecologia Digitale</h2>
              <p className="text-gray-500 text-xs">Una missione, non un trend</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white p-2 rounded-lg hover:bg-white/5 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-8">

          {/* Intro */}
          <div>
            <p className="text-gray-300 leading-relaxed">
              Lo sapevi? Ogni azione digitale ha un <strong className="text-white">impatto ambientale reale</strong>.
              Non si vede, non si tocca, ma c'e. La buona notizia? Bastano piccole scelte quotidiane per fare la differenza.
            </p>
          </div>

          {/* Email Impact */}
          <div className="bg-white/5 rounded-xl p-5 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-[#EA4335]" />
              <h3 className="text-white font-bold">L'email che non ti aspetti</h3>
            </div>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center bg-[#EA4335]/10 rounded-xl p-4">
                <p className="text-3xl font-black text-[#EA4335]">4g</p>
                <p className="text-gray-400 text-xs mt-1">CO2 per ogni email</p>
              </div>
              <div className="text-center bg-[#FBBC04]/10 rounded-xl p-4">
                <p className="text-3xl font-black text-[#FBBC04]">50g</p>
                <p className="text-gray-400 text-xs mt-1">CO2 con allegato pesante</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Secondo uno studio di Mike Berners-Lee (autore di <em>"How Bad Are Bananas?"</em>), una email standard produce circa <strong className="text-white">4 grammi di CO2</strong>.
              Con un allegato pesante? Fino a <strong className="text-white">50 grammi</strong>.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed mt-2">
              Nel mondo si inviano <strong className="text-white">333 miliardi di email al giorno</strong>.
              Quante sono solo per dire "Ok", "Ricevuto", "Grazie"? Circa il 30%.
              Sono <strong className="text-white">100 miliardi di email inutili</strong> — ogni giorno.
            </p>
            <div className="mt-3 bg-[#EA4335]/5 rounded-lg p-3 border border-[#EA4335]/20">
              <p className="text-[#EA4335] text-xs font-bold">
                100 miliardi x 4g = 400.000 tonnellate di CO2 al giorno. Solo per dire "ok".
              </p>
            </div>
          </div>

          {/* WhatsApp Impact */}
          <div className="bg-white/5 rounded-xl p-5 border border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <Smartphone className="w-6 h-6 text-[#34A853]" />
              <h3 className="text-white font-bold">Il peso di 3 emoji su WhatsApp</h3>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Un messaggio WhatsApp produce circa <strong className="text-white">0,2 grammi di CO2</strong>.
              Sembra poco? In Italia inviamo <strong className="text-white">10 miliardi di messaggi al mese</strong>.
            </p>
            <p className="text-gray-400 text-sm leading-relaxed mt-2">
              Tre emoji di pollice in su al posto di un "si" occupano piu dati, richiedono piu rendering e consumano piu batteria.
              <strong className="text-white"> Un semplice "si" inquina meno di tre emoji.</strong>
            </p>
          </div>

          {/* Dark Mode */}
          <div className="bg-white/5 rounded-xl p-5 border border-[#34A853]/30">
            <div className="flex items-center gap-3 mb-4">
              <Moon className="w-6 h-6 text-[#4285F4]" />
              <h3 className="text-white font-bold">Dark Mode: non e solo estetica</h3>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="text-center bg-white/5 rounded-xl p-3">
                <Monitor className="w-5 h-5 text-[#4285F4] mx-auto mb-1" />
                <p className="text-white font-bold text-sm">-60%</p>
                <p className="text-gray-500 text-[10px]">energia schermo OLED</p>
              </div>
              <div className="text-center bg-white/5 rounded-xl p-3">
                <Leaf className="w-5 h-5 text-[#34A853] mx-auto mb-1" />
                <p className="text-white font-bold text-sm">+30%</p>
                <p className="text-gray-500 text-[10px]">autonomia batteria</p>
              </div>
              <div className="text-center bg-white/5 rounded-xl p-3">
                <Moon className="w-5 h-5 text-[#FBBC04] mx-auto mb-1" />
                <p className="text-white font-bold text-sm">-50%</p>
                <p className="text-gray-500 text-[10px]">luce blu emessa</p>
              </div>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Google ha confermato che su schermi OLED il dark mode risparmia fino al <strong className="text-white">60% di energia</strong>.
              Meno energia = meno CO2. Meno luce blu = meno affaticamento oculare.
              <strong className="text-white"> Non e un capriccio estetico. E una scelta responsabile.</strong>
            </p>
          </div>

          {/* Quick tips */}
          <div>
            <h3 className="text-white font-bold mb-3">5 gesti digitali che fanno la differenza</h3>
            <div className="space-y-2">
              {[
                { q: "Rispondi \"si\" invece di tre emoji", a: "Un carattere di testo pesa 1 byte. Tre emoji pesano 12 byte. Moltiplicalo per miliardi di messaggi al giorno. Il testo semplice e piu veloce da trasmettere, richiede meno energia lato server e meno rendering lato device." },
                { q: "Cancella le email vecchie", a: "Ogni email archiviata occupa spazio su un server sempre acceso. 1 GB di email = circa 14 kg di CO2 all'anno solo per lo storage. Fai pulizia e libera il pianeta." },
                { q: "Attiva il dark mode ovunque", a: "Su smartphone, tablet, laptop e browser. Google, YouTube, Instagram, WhatsApp, Twitter: tutti lo supportano. Se hai uno schermo OLED il risparmio e immediato e misurabile." },
                { q: "Disiscriviti dalle newsletter che non leggi", a: "Ogni newsletter non letta e un'email che attraversa data center, router e antenne per finire nel cestino. In media riceviamo 120 email al giorno. Quante ne leggiamo davvero?" },
                { q: "Invia una nota vocale invece di 20 messaggi", a: "Una nota vocale compressa pesa meno di 20 messaggi di testo separati con emoji, foto e sticker. E si capisce meglio il tono e l'urgenza del problema. Per questo abbiamo creato il microfono su schedabusiness.com." },
              ].map((item, i) => (
                <div key={i} className="bg-white/5 rounded-xl border border-white/5 overflow-hidden">
                  <button
                    onClick={() => toggle(i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5 transition-colors"
                  >
                    <span className="text-white text-sm font-medium flex items-center gap-2">
                      <span className="text-[#34A853] font-bold">{i + 1}.</span> {item.q}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${expanded === i ? 'rotate-180' : ''}`} />
                  </button>
                  {expanded === i && (
                    <div className="px-4 pb-3">
                      <p className="text-gray-400 text-xs leading-relaxed">{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Mission statement */}
          <div className="bg-gradient-to-br from-[#34A853]/10 to-[#4285F4]/10 rounded-xl p-5 border border-[#34A853]/20 text-center">
            <Leaf className="w-8 h-8 text-[#34A853] mx-auto mb-3" />
            <p className="text-white font-bold text-lg mb-2">La nostra missione</p>
            <p className="text-gray-300 text-sm leading-relaxed max-w-md mx-auto">
              schedabusiness.com e dark by design. Non solo perche e bello, ma perche <strong>ogni pixel nero su schermo OLED e un pixel spento</strong>.
              Meno energia consumata, meno CO2 prodotta.
              Ogni volta che navighi il nostro sito, stai risparmiando energia. Non e marketing. E scelta.
            </p>
            <p className="text-[#34A853] font-bold text-xs mt-4">
              Non ti chiediamo di cambiare vita. Ti chiediamo di cambiare un "ok" in un "si".
            </p>
          </div>

          {/* Sources */}
          <div className="border-t border-white/10 pt-4">
            <p className="text-gray-600 text-[10px]">
              Fonti: Mike Berners-Lee "How Bad Are Bananas?" (2020) — Google Android Dev Blog su OLED energy (2018) — The Shift Project "Lean ICT" (2019) — Statista Global Email Traffic (2024) — Purdue University dark mode study (2021).
            </p>
          </div>

        </div>
      </div>
    </div>
  );
};

export default EcologiaDigitaleArticle;
